import { addDays, format } from 'date-fns';
import { generateDocumentCommodities } from '../../components/calculator/utils/documentCommodityUtils';

export interface ApiResponse {
  success?: boolean;
  message?: string;
  data?: any;
}

export interface PaymentDetails {
  method: 'card' | 'bank' | 'wallet';
  selectedPaymentMethodId?: string;
  cardNumber: string;
  cardholderName: string;
  expiryDate: string;
  cvv: string;
  billingAddress: { country: string; city: string; street: string; zipCode: string };
  savePaymentMethod: boolean;
}

export interface OrderSummary {
  sender: any;
  recipient: any;
  service: string;
  parcel: { weight: string; dimensions: string };
  parcels?: any[];
  price: { shipping: number; insurance: number; tax: number; total: number };
  pickupDate: string;
  currency: string;
  isDocument: boolean;
  carrier?: string;
  deliveryEstimate?: string;
  referenceNumber?: string;
}

export const STEPS = ['Quote', 'Shipping', 'Payment', 'Confirmation'];

export const formatAddressSummary = (a: any): string => {
  if (!a) return '—';
  const parts = [
    a.contactName,
    [a.street, a.houseNo].filter(Boolean).join(' '),
    [a.city?.name || a.city_other, a.state?.name, a.zipCode].filter(Boolean).join(', '),
    a.country?.name || a.country,
  ].filter(Boolean);
  return parts.join(' · ');
};

export const prepareOrderData = (incoming: any) => {
  const allowDraft =
    typeof incoming.allowDraft === 'boolean' ? incoming.allowDraft : true;
  const parcelsWithDimensions = Array.isArray(incoming.parcels)
    ? incoming.parcels.map((parcel: any) => ({
        ...parcel,
        dimensionsDisplay:
          parcel.length && parcel.width && parcel.height
            ? `${parcel.length} × ${parcel.width} × ${parcel.height} cm`
            : 'Dimensions not available',
        items: parcel.items || [],
      }))
    : incoming.parcels;
  return { ...incoming, allowDraft, parcels: parcelsWithDimensions };
};

export const computeOrderSummary = (prepared: any): OrderSummary => {
  const currency = prepared.selectedRate?.currency || 'ILS';
  const isDocument = prepared.shippingType === 'document';

  let itemDetails = {
    weight: `${prepared.orderDetails?.totalWeight || '0'} kg`,
    dimensions: 'Dimensions not available',
  };
  if (isDocument) {
    const docSizes: Record<string, string> = {
      large_envelope: 'Large envelope (25,30,5 cm)',
      small_envelope: 'Small envelope (20,25,5 cm)',
      folder: 'Document folder',
      tube: 'Document tube',
    };
    const docSize = prepared.documents?.[0]?.size
      ? docSizes[prepared.documents[0].size] || prepared.documents[0].size
      : 'Standard document';
    itemDetails = { weight: itemDetails.weight, dimensions: docSize };
  } else if (prepared.parcels?.[0]) {
    const p = prepared.parcels[0];
    if (p.length && p.width && p.height) {
      itemDetails.dimensions = `${p.length} × ${p.width} × ${p.height} cm`;
    }
  }

  return {
    sender: prepared.addresses.from,
    recipient: prepared.addresses.to,
    service: prepared.selectedRate?.name || 'Service not specified',
    carrier:
      prepared.selectedRate?.carrier ||
      prepared.selectedRate?.provider_display_name,
    parcel: itemDetails,
    parcels: prepared.parcels || [],
    price: {
      shipping: prepared.price?.shipping || 0,
      insurance: prepared.price?.insurance || 0,
      tax: prepared.price?.tax || 0,
      total: prepared.price?.total || 0,
    },
    pickupDate: prepared.selectedRate?.deliveryTime || 'Not specified',
    deliveryEstimate:
      prepared.orderDetails?.estimatedDelivery || prepared.selectedRate?.deliveryTime,
    currency,
    isDocument,
    referenceNumber:
      prepared.referenceNumber || `REF-${Date.now().toString().substring(6)}`,
  };
};

export const buildOrderPayload = (
  orderData: any,
  orderSummary: OrderSummary,
  paymentDetails: PaymentDetails,
  paymentToken: string | null,
  billingName: string,
  options?: { includePayment?: boolean }
): any => {
  if (!orderData) throw new Error('Order data is unavailable');
  const parcelsData = orderData.parcels?.map((p: any) => ({
    ...p,
    length: p.length,
    width: p.width,
    height: p.height,
    items: p.items || [],
  }));
  const documentsData = orderData.documents?.map((doc: any) => ({
    ...doc,
    physicalWeight: doc.weight || 0.5,
    dimensionalWeight: 0,
    length: 0,
    width: 0,
    height: 0,
    items:
      doc.items ||
      generateDocumentCommodities(
        [doc],
        orderData.addresses?.from?.country?.name ||
          orderData.addresses?.from?.country ||
          orderData.origin ||
          'United States'
      ),
  }));
  const { allowDraft: _ad, save_as_draft: _sd, ...rest } = orderData;
  const payload: any = {
    ...rest,
    parcels: parcelsData?.length ? parcelsData : documentsData || [],
    documents: documentsData,
    referenceNumber:
      orderSummary.referenceNumber ||
      rest.referenceNumber ||
      `REF-${Date.now().toString().substring(6)}`,
  };
  if (options?.includePayment) {
    payload.paymentMethod = paymentDetails.method;
    payload.paymentMethodId =
      paymentDetails.selectedPaymentMethodId || paymentToken;
    payload.billingName = billingName;
  }
  if (
    payload.deliveryEstimate &&
    typeof payload.deliveryEstimate === 'string' &&
    payload.deliveryEstimate.includes('Days')
  ) {
    const days = parseInt(payload.deliveryEstimate.split(' ')[0], 10);
    if (!isNaN(days)) {
      payload.deliveryEstimate = format(addDays(new Date(), days), 'yyyy-MM-dd');
    }
  }
  if (
    payload.selectedRate?.deliveryTime &&
    typeof payload.selectedRate.deliveryTime === 'string' &&
    payload.selectedRate.deliveryTime.includes('Days')
  ) {
    const days = parseInt(payload.selectedRate.deliveryTime.split(' ')[0], 10);
    if (!isNaN(days)) {
      payload.selectedRate.deliveryTime = format(addDays(new Date(), days), 'yyyy-MM-dd');
    }
  }
  return payload;
};

export const validatePaymentForm = (
  paymentDetails: PaymentDetails,
  balance: number,
  total: number
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!paymentDetails.billingAddress.city)
    errors['billingAddress.city'] = 'City is required';
  if (!paymentDetails.billingAddress.street)
    errors['billingAddress.street'] = 'Street is required';
  if (!paymentDetails.billingAddress.zipCode)
    errors['billingAddress.zipCode'] = 'ZIP/postal code is required';
  if (paymentDetails.method === 'wallet') {
    if (!paymentDetails.selectedPaymentMethodId)
      errors.selectedPaymentMethodId = 'Please select a payment method';
    if (balance < total) errors.balance = 'Insufficient wallet balance';
  }
  return errors;
};
